import React, { useState, useEffect } from 'react';
import { X, Settings, Cookie } from 'lucide-react';
import Cookies from 'js-cookie';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });

  // Fungsi untuk menghasilkan token sementara
  const generateTemporaryToken = () => {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  };

  useEffect(() => {
    // Periksa apakah cookie consent sudah pernah diatur sebelumnya
    const storedConsent = localStorage.getItem('papakilo_cookie_consent');
    
    if (!storedConsent) {
      // Tampilkan cookie consent setelah 2 detik
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const fullConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };

    saveCookieConsent(fullConsent);
  };

  const handleSavePreferences = () => {
    saveCookieConsent(cookiePreferences);
  };

  const saveCookieConsent = (preferences) => {
  // Simpan preferensi ke localStorage
  localStorage.setItem('papakilo_cookie_consent', JSON.stringify(preferences));
  
  // Generate dan simpan token sementara
  const token = generateTemporaryToken();
  
  // Simpan token dalam cookie
  Cookies.set('papakilo_session_token', token, { 
    expires: 1, // Token berlaku 1 hari
    secure: process.env.NODE_ENV === 'production', // Hanya di HTTPS saat production
    sameSite: 'strict'
  });

  // Sembunyikan modal
  setIsVisible(false);
  setIsManageModalOpen(false);

  // Aktifkan cookie sesuai preferensi
  Object.entries(preferences).forEach(([type, enabled]) => {
    if (enabled) {
      enableCookies(type);
    }
  });
};

  const enableCookies = (type) => {
  switch(type) {
    case 'necessary':
      // Cookies yang diperlukan untuk fungsi dasar situs
      Cookies.set('papakilo_essential_cookies', 'enabled', { 
        expires: 365, 
        sameSite: 'strict' 
      });
      break;

    case 'functional':
      // Cookies untuk meningkatkan fungsionalitas
      Cookies.set('papakilo_functional_cookies', 'enabled', { 
        expires: 90, 
        sameSite: 'strict' 
      });
      // Contoh: Simpan preferensi pengguna
      localStorage.setItem('user_preferences', JSON.stringify({
        language: navigator.language,
        theme: 'default'
      }));
      break;

    case 'analytics':
      // Cookies untuk analitik (misalnya Google Analytics)
      Cookies.set('papakilo_analytics_cookies', 'enabled', { 
        expires: 30, 
        sameSite: 'strict' 
      });
      // Contoh integrasi Google Analytics
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      }
      break;

    case 'marketing':
      // Cookies untuk pemasaran dan pelacakan
      Cookies.set('papakilo_marketing_cookies', 'enabled', { 
        expires: 60, 
        sameSite: 'strict' 
      });
      // Contoh: Aktifkan pixel Facebook atau Google Ads
      if (window.fbq) {
        window.fbq('consent', 'grant');
      }
      break;

    default:
      console.warn(`Tipe cookie tidak dikenal: ${type}`);
  }

  // Log aktifasi cookie
  console.log(`Cookie tipe ${type} diaktifkan`);
};

  // Modal Manajemen Cookie
  const ManageCookieModal = () => (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Cookie className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Preferensi Cookie</h2>
          </div>
          <button 
            onClick={() => setIsManageModalOpen(false)}
            className="hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          <p className="text-gray-600 mb-6">
            Kami menggunakan cookie untuk meningkatkan pengalaman pengguna dan menganalisis lalu lintas situs. 
            Anda dapat menyesuaikan preferensi cookie Anda di bawah ini.
          </p>

          {/* Cookie Types */}
          <div className="space-y-4">
            {[
              { 
                type: 'necessary', 
                name: 'Cookie Esensial', 
                description: 'Cookie yang diperlukan untuk fungsi dasar situs web dan tidak dapat dinonaktifkan.',
                disabled: true
              },
              { 
                type: 'functional', 
                name: 'Cookie Fungsional', 
                description: 'Meningkatkan fungsionalitas dan personalisasi situs.'
              },
              { 
                type: 'analytics', 
                name: 'Cookie Analitik', 
                description: 'Membantu kami memahami cara pengunjung berinteraksi dengan situs.'
              },
              { 
                type: 'marketing', 
                name: 'Cookie Pemasaran', 
                description: 'Digunakan untuk iklan dan pelacakan konten personalisasi.'
              }
            ].map((cookie) => (
              <div 
                key={cookie.type} 
                className="bg-gray-100 rounded-xl p-4 flex justify-between items-center hover:bg-gray-200 transition-colors"
              >
                <div className="flex-grow pr-4">
                  <h3 className="font-semibold text-lg text-gray-800">{cookie.name}</h3>
                  <p className="text-sm text-gray-600">{cookie.description}</p>
                </div>
                
                {/* Toggle Switch */}
                <div className="relative">
                  <input
                    type="checkbox"
                    id={`cookie-${cookie.type}`}
                    checked={cookiePreferences[cookie.type] || cookie.type === 'necessary'}
                    onChange={() => {
                      if (!cookie.disabled) {
                        setCookiePreferences(prev => ({
                          ...prev,
                          [cookie.type]: !prev[cookie.type]
                        }));
                      }
                    }}
                    disabled={cookie.disabled}
                    className="sr-only peer"
                  />
                  <label 
                    htmlFor={`cookie-${cookie.type}`}
                    className={`
                      w-14 h-8 rounded-full cursor-pointer
                      ${cookie.disabled 
                        ? 'bg-gray-300' 
                        : 'bg-gray-300 peer-checked:bg-blue-600'}
                      relative inline-block
                      transition-colors duration-200
                      after:content-[''] 
                      after:absolute 
                      after:top-1 
                      after:left-1 
                      after:bg-white 
                      after:rounded-full 
                      after:h-6 
                      after:w-6 
                      after:transition-all 
                      after:duration-200
                      ${(cookiePreferences[cookie.type] || cookie.disabled) 
                        ? 'after:translate-x-6' 
                        : ''}
                    `}
                  ></label>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={() => setIsManageModalOpen(false)}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Batal
            </button>
            <button
              onClick={handleSavePreferences}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Simpan Preferensi
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Jika tidak perlu ditampilkan, kembalikan null
  if (!isVisible) return null;

  return (
    <>
      {/* Modal Manajemen Cookie */}
      {isManageModalOpen && <ManageCookieModal />}
      
      <div className="fixed bottom-0 left-0 right-0 z-50 p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 w-full"></div>
          <div className="p-6 flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0 md:mr-6 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-3">
                <Cookie className="w-8 h-8 text-blue-600 mr-3" />
                <h2 className="text-xl font-bold text-gray-800">
                  Kami Menggunakan Cookie
                </h2>
              </div>
              <p className="text-gray-600 text-center md:text-left">
                Kami menggunakan cookie untuk meningkatkan pengalaman pengguna 
                dan menganalisis lalu lintas situs.
              </p>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Terima Semua
              </button>
              <button
                onClick={() => setIsManageModalOpen(true)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
              >
                <Settings className="mr-2 h-5 w-5" />
                Kelola Cookie
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;